import { z } from "zod";

import { ApiSuccessSchema } from "../../common/response.schema";
import {
  AbortPromptEvalRunResponseSchema,
  CreatePromptEvalRunBodySchema,
  CreatePromptEvalRunResponseSchema,
  GetPromptEvalCaseResponseSchema,
  GetPromptEvalRunResponseSchema,
  GetPromptEvalRunResultsResponseSchema,
  ListPromptEvalRunsParamsSchema,
  ListPromptEvalRunsResponseSchema,
  PreviewPromptEvalRunBodySchema,
  PreviewPromptEvalRunResponseSchema
} from "../../public/v1/schemas/prompt-eval-run.schemas";

/**
 * ZPromptEvalRuns — internal contract for Prompt Lab eval runs, the browser
 * twin of `nexus eval run`. Same shapes as the public v1 surface: the schemas
 * come from `public/v1/schemas/prompt-eval-run.schemas`, the idiom the
 * golden-conversations and prompt-variants contracts already follow, wrapped in
 * the internal success envelope.
 *
 * All routes are nested under /api/prompt-eval/runs and every one is gated on
 * the PROMPT_EVAL feature flag by `PromptEvalRunsController`.
 *
 * Money is in ten-thousandths of a USD throughout (1500 = $0.15). A run's
 * `rollup` is null until the run reaches a terminal status, and a variant's
 * `meanScore` / `passRate` are null — never 0 — when nothing scored.
 */
export const ZPromptEvalRuns = {
  /** GET /api/prompt-eval/runs — the org's runs, optionally for one agent, newest first */
  List: {
    method: "GET" as const,
    path: "/api/prompt-eval/runs",
    Query: ListPromptEvalRunsParamsSchema,
    Response: ApiSuccessSchema(ListPromptEvalRunsResponseSchema)
  },

  /**
   * POST /api/prompt-eval/runs/preview — the matrix a create would build
   * (cases, candidates, expanded selection, warnings). Spends nothing.
   */
  Preview: {
    method: "POST" as const,
    path: "/api/prompt-eval/runs/preview",
    Body: PreviewPromptEvalRunBodySchema,
    Response: ApiSuccessSchema(PreviewPromptEvalRunResponseSchema)
  },

  /**
   * POST /api/prompt-eval/runs — create and queue a run. Real spend: every case
   * replays a golden prefix against a live deployment, tools included.
   */
  Create: {
    method: "POST" as const,
    path: "/api/prompt-eval/runs",
    Body: CreatePromptEvalRunBodySchema,
    Response: ApiSuccessSchema(CreatePromptEvalRunResponseSchema)
  },

  /** GET /api/prompt-eval/runs/:runId — the run, its snapshots and accrued cost */
  Get: {
    method: "GET" as const,
    path: "/api/prompt-eval/runs/:runId",
    PathVars: z.object({ runId: z.string() }),
    Response: ApiSuccessSchema(GetPromptEvalRunResponseSchema)
  },

  /** POST /api/prompt-eval/runs/:runId/abort — stop; finished cases are kept */
  Abort: {
    method: "POST" as const,
    path: "/api/prompt-eval/runs/:runId/abort",
    PathVars: z.object({ runId: z.string() }),
    Response: ApiSuccessSchema(AbortPromptEvalRunResponseSchema)
  },

  /** GET /api/prompt-eval/runs/:runId/results — every cell with its scores, plus the rollup */
  Results: {
    method: "GET" as const,
    path: "/api/prompt-eval/runs/:runId/results",
    PathVars: z.object({ runId: z.string() }),
    Response: ApiSuccessSchema(GetPromptEvalRunResultsResponseSchema)
  },

  /** GET /api/prompt-eval/runs/:runId/cases/:caseId — one cell in full: golden vs candidate */
  GetCase: {
    method: "GET" as const,
    path: "/api/prompt-eval/runs/:runId/cases/:caseId",
    PathVars: z.object({ runId: z.string(), caseId: z.string() }),
    Response: ApiSuccessSchema(GetPromptEvalCaseResponseSchema)
  }
} as const;
